import { keepPreviousData, skipToken, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import { isEmptyString } from '@/utils/string';
import { ATTRIBUTES_PAGE_SIZE, attributeKeys, type AttributeListScope } from './entity';

type AttributesListQuery = NonNullable<
  NonNullable<NonNullable<Parameters<typeof hydraUiApi.attributes.list>[0]>['params']>['query']
>;

function buildListQuery(page: number, scope: AttributeListScope): AttributesListQuery {
  const query: AttributesListQuery = { page, size: ATTRIBUTES_PAGE_SIZE };
  if (scope.search != null) query.search = scope.search;
  if (scope.inUseOnly === true) query.inUseOnly = true;
  if (scope.objectTypeId != null) query.objectType = Number(scope.objectTypeId);
  return query;
}

/** @public */
export async function getAttributeNamesPage(
  workspaceId: number,
  { page, ...scope }: AttributeListScope & { page: number },
): Promise<string[]> {
  const raw = await hydraUiApi.attributes.list({
    params: {
      path: { environment_id: String(workspaceId) },
      query: buildListQuery(page, scope),
    },
  });
  return raw.attributes.map((attribute) => attribute.id);
}

/**
 * The server-paged attribute-name catalog. The first page loads on mount;
 * callers pull further pages on demand with `fetchNextPage` (a short page
 * ends the catalog). A failed page load keeps `hasNextPage` true so the next
 * pull retries. Never served from cache (`staleTime: 0`) — attributes are
 * written by ingestion, not this app, so a cached catalog can be stale.
 *
 * A changed scope (search term, in-use filter, object type) is a new catalog
 * starting from page 1; the previous scope's pages stay visible while the
 * first page loads, so a keystroke doesn't blank a picker.
 *
 * @public
 */
export function useAttributeCatalog(workspaceId: number, scope: AttributeListScope = {}) {
  return useInfiniteQuery({
    queryKey: attributeKeys.catalog(workspaceId, scope),
    queryFn: ({ pageParam }) => getAttributeNamesPage(workspaceId, { page: pageParam, ...scope }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.length < ATTRIBUTES_PAGE_SIZE ? undefined : lastPageParam + 1,
    staleTime: 0,
    placeholderData: keepPreviousData,
  });
}

/** @public */
export async function getAttributeValues(workspaceId: number, attribute: string): Promise<string[]> {
  const raw = await hydraUiApi.valueLists.getAttributesValues({
    params: { path: { environment_id: String(workspaceId) }, query: { attribute } },
  });
  return raw.values;
}

/**
 * Autosuggest values for one profile attribute. Only fires while an attribute
 * is named; a failed load yields no suggestions rather than an error state —
 * the value input accepts free text either way.
 *
 * @public
 */
export function useAttributeValues(workspaceId: number, attribute: string) {
  const active = !isEmptyString(attribute);
  return useQuery({
    queryKey: attributeKeys.values(workspaceId, attribute),
    queryFn: active ? () => getAttributeValues(workspaceId, attribute) : skipToken,
    staleTime: 0,
    retry: false,
  });
}

async function getObjectAttributeValues(workspaceId: number, objectTypeId: string, attribute: string) {
  const raw = await hydraUiApi.valueLists.getObjectAttributesValues({
    params: {
      path: { environment_id: String(workspaceId), type_id: objectTypeId },
      query: { attribute },
    },
  });
  return raw.values;
}

/** Autosuggest values for one object attribute. Free text remains available if this request fails. */
export function useObjectAttributeValues(workspaceId: number, objectTypeId: string, attribute: string) {
  const active = !isEmptyString(attribute);
  return useQuery({
    queryKey: attributeKeys.objectValues(workspaceId, objectTypeId, attribute),
    queryFn: active ? () => getObjectAttributeValues(workspaceId, objectTypeId, attribute) : skipToken,
    staleTime: 0,
    retry: false,
  });
}

/**
 * Server-side attribute-name search (first page of matches). Only fires while
 * there is a search term; previous matches stay visible while a narrower term
 * loads, so a keystroke doesn't blank the results.
 *
 * @public
 */
export function useAttributeNameSearch(workspaceId: number, search: string) {
  const active = !isEmptyString(search);
  return useQuery({
    queryKey: active ? attributeKeys.search(workspaceId, search) : attributeKeys.searchInactive(workspaceId),
    queryFn: active ? () => getAttributeNamesPage(workspaceId, { page: 1, search }) : skipToken,
    staleTime: 0,
    placeholderData: keepPreviousData,
  });
}
